// Панель фильтра раздела «Буи»: типы платформ, что есть в КАДРЕ.
//
// Список строится по фасетам ответа, а не по всему каталогу типов: тип,
// которого в кадре нет, в панели не нужен, а тип, который есть, обязан быть
// назван — иначе, выбрав один, переключиться на другой было бы не по чему.

import { formatNumber, useLang, useT } from '../i18n'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { MapPanel } from '../map/MapPanel'
import type { BoundaryMeta, BoundaryToggles as Toggles } from '../map/useBoundaryOverlays'
import { legendItems } from './buoyLayer'
import { RESIDUAL_LAYERS, legendBands, residualScale } from './buoyResidual'
import type { BuoyFilter } from './buoyFilter'

interface Props {
  facets: Array<{ value: string; count: number }>
  filter: BuoyFilter
  shown: number
  retired: number
  onToggleType: (type: string) => void
  onToggleStale: () => void
  /** Слой раскраски по невязке; `null` — обычный вид (цвет = свежесть). */
  residual: string | null
  onResidual: (layer: string | null) => void
  /** Сколько точек кадра получили невязку ВООБЩЕ. */
  scored: number
  /** Рубежи: границы ИЭЗ, промысловые и запретные районы. */
  boundaries: Toggles
  onToggleBoundary: (key: keyof Toggles) => void
  boundaryMeta: BoundaryMeta
}

export function FilterPanel({
  facets, filter, shown, retired, onToggleType, onToggleStale,
  residual, onResidual, scored, boundaries, onToggleBoundary, boundaryMeta,
}: Props) {
  const t = useT()
  const items = legendItems(facets)

  return (
    <MapPanel storageKey="buoys.panel" className="w-[248px]" title={t('buoys.filter.title')} meta={shown}>
    <div className="px-3 pb-3 text-[12px]">

      <ul className="space-y-1">
        {items.map(item => {
          const active = filter.types.includes(item.type)
          return (
            <li key={item.type}>
              <button
                type="button"
                onClick={() => onToggleType(item.type)}
                className={`flex w-full items-baseline justify-between gap-2 rounded px-1.5 py-1 text-left ${
                  active ? 'bg-hull text-foam' : 'text-mist hover:text-foam'
                }`}
              >
                <span className="min-w-0 truncate">{item.label}</span>
                <span className="tnum shrink-0 text-faint">{item.count}</span>
              </button>
            </li>
          )
        })}
        {items.length === 0 && <li className="text-faint">{t('buoys.filter.empty')}</li>}
      </ul>

      <label className="mt-2 flex items-center gap-2 border-t border-hull-hair pt-2 text-mist">
        <input type="checkbox" checked={filter.hideStale} onChange={onToggleStale} />
        <span>{t('buoys.filter.onlyFresh')}</span>
      </label>

      {retired > 0 && (
        <div className="mt-1 text-[11px] leading-snug text-faint">
          {t('buoys.filter.retiredHint', { n: retired })}
        </div>
      )}

      {/* Раскраска по невязке. Отдельным блоком, а не ещё одним тумблером
          фильтра: фильтр решает, КОГО показать, а это — ЧТО значит цвет. */}
      <div className="mt-2 border-t border-hull-hair pt-2">
        <div className="text-mist">{t('buoys.resid.title')}</div>
        <div className="mt-1 flex gap-1">
          <ModeButton
            active={residual === null}
            label={t('buoys.resid.mode.fresh')}
            onClick={() => onResidual(null)}
          />
          {RESIDUAL_LAYERS.map(layer => (
            <ModeButton
              key={layer}
              active={residual === layer}
              label={t(`buoys.resid.mode.${layer}`)}
              onClick={() => onResidual(layer)}
            />
          ))}
        </div>

        {residual && <ResidualLegend layer={residual} scored={scored} shown={shown} />}
      </div>

      {/* Рубежи: границы ИЭЗ, промысловые и запретные районы. Не фильтр и не
          раскраска — ПОДЛОЖКА: измерение сравнивают с моделью, а вопросы «в
          чьей воде стоит платформа» и «по каким правилам здесь ловят»
          возникают ровно здесь же. */}
      <div className="mt-2 border-t border-hull-hair pt-2">
        <BoundaryToggles on={boundaries} onToggle={onToggleBoundary} meta={boundaryMeta} />
      </div>
    </div>
    </MapPanel>
  )
}

function ModeButton({ active, label, onClick }: {
  active: boolean; label: string; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-1.5 py-0.5 text-[11px] ${
        active ? 'bg-hull text-foam' : 'text-mist hover:text-foam'
      }`}
    >
      {label}
    </button>
  )
}

/** Легенда невязки. Числа на ней — обязательны: цвет без шкалы говорит только
 *  «где-то расходится», а вопрос всегда «на сколько». */
function ResidualLegend({ layer, scored, shown }: {
  layer: string; scored: number; shown: number
}) {
  const t = useT()
  // Через `Intl`, а не `toFixed`: в русском кабинете разделителем обязана быть
  // запятая, и своя арифметика форматирования её теряет.
  const { lang } = useLang()
  const num = (v: number) => formatNumber(lang, v, 2)
  const scale = residualScale(layer)

  return (
    <div className="mt-2">
      <ul className="space-y-[2px]">
        {legendBands(layer).map((band, i) => (
          <li key={i} className="flex items-center gap-1.5 text-[11px] text-mist">
            <span
              className="inline-block h-2.5 w-4 shrink-0 rounded-sm"
              style={{ background: band.color }}
            />
            <span className="tnum">
              {band.from == null
                ? `< ${num(band.to as number)}`
                : band.to == null
                  ? `> ${num(band.from)}`
                  : `${num(band.from)} … ${num(band.to)}`}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-1 text-[11px] leading-snug text-faint">
        {/* Единица показывается КАК ЕСТЬ, как и в карточке: она приезжает от
            источника, и переводить её значило бы заводить второй словарь. */}
        {scale.unit} · {t('buoys.resid.sign')}
      </div>
      {/* Сколько точек кадра вообще сравнимы. Без этого числа кадр без единого
          ореола читается как «модель везде точна», тогда как чаще всего
          сравнивать просто не с чем. */}
      <div className="mt-1 text-[11px] leading-snug text-faint">
        {t('buoys.resid.scored', { n: scored, total: shown })}
      </div>
    </div>
  )
}
